import {
  Box,
  Collapse,
  Group,
  UnstyledButton,
  createStyles,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownLeft, ChevronRight, Icon } from 'tabler-icons-react';
import { SubLink } from '../../../type/navlink.types';

const useStyles = createStyles((theme, _params, getRef) => {
  const icon = getRef('icon');

  return {
    control: {
      display: 'block',
      alignItems: 'center',
      textDecoration: 'none',
      fontSize: theme.fontSizes.md,
      color:
        theme.colorScheme === 'dark'
          ? theme.colors.dark[1]
          : theme.colors.gray[7],
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
      borderRadius: theme.radius.sm,
      fontWeight: 500,

      '&:hover': {
        backgroundColor:
          theme.colorScheme === 'dark'
            ? theme.colors.dark[6]
            : theme.colors.gray[0],
        color: theme.colorScheme === 'dark' ? theme.white : theme.black,

        [`& .${icon}`]: {
          color: theme.colorScheme === 'dark' ? theme.white : theme.black,
        },
      },
    },

    chevron: {
      transition: 'transform 200ms ease',
    },

    link: {
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
      fontSize: theme.fontSizes.md,
      paddingLeft: 31,
      marginLeft: 31,
      borderLeft: `1px solid ${
        theme.colorScheme === 'dark'
          ? theme.colors.dark[4]
          : theme.colors.gray[3]
      }`,
      color:
        theme.colorScheme === 'dark'
          ? theme.colors.dark[1]
          : theme.colors.gray[7],
      padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
      borderRadius: theme.radius.sm,
      fontWeight: 500,

      '&:hover': {
        backgroundColor:
          theme.colorScheme === 'dark'
            ? theme.colors.dark[6]
            : theme.colors.gray[0],
        color: theme.colorScheme === 'dark' ? theme.white : theme.black,

        [`& .${icon}`]: {
          color: theme.colorScheme === 'dark' ? theme.white : theme.black,
        },
      },
    },

    linkIcon: {
      ref: icon,
      color:
        theme.colorScheme === 'dark'
          ? theme.colors.dark[2]
          : theme.colors.gray[6],
      marginRight: theme.spacing.sm,
    },

    linkActive: {
      '&, &:hover': {
        backgroundColor: theme.fn.variant({
          variant: 'light',
          color: theme.primaryColor,
        }).background,
        color: theme.fn.variant({ variant: 'light', color: theme.primaryColor })
          .color,
        [`& .${icon}`]: {
          color: theme.fn.variant({
            variant: 'light',
            color: theme.primaryColor,
          }).color,
        },
      },
    },
  };
});

interface LinksGroupProps {
  icon: Icon;
  label: string;
  initiallyOpened?: boolean;
  links?: { label: string; link: string }[];
  active: string;
  setActive: (label: string) => void;
}

function LinksGroup({
  icon: Icon,
  label,
  initiallyOpened,
  links,
  active,
  setActive,
}: LinksGroupProps) {
  const { classes, cx, theme } = useStyles();
  const hasLinks = Array.isArray(links);
  const [opened, setOpened] = useState(initiallyOpened || false);
  const ChevronIcon = theme.dir === 'ltr' ? ChevronRight : ChevronDownLeft;
  const [initialLoad, setInitialLoad] = useState(true);

  //useEffect with [] dependency does not work
  //which is why we have to use this way
  useEffect(() => {
    if (!initialLoad) {
      return;
    }

    if (JSON.stringify(links).includes(active)) {
      setOpened(true);
    }

    return () => {
      setInitialLoad(false);
    };
  }, [active]);

  const items = (hasLinks ? links : []).map((link) => {
    return (
      <Link
        className={cx(classes.link, {
          [classes.linkActive]: link.label === active,
        })}
        to={link.link}
        onClick={() => {
          setActive(link.label);
        }}
        key={link.label}
      >
        {link.label}
      </Link>
    );
  });

  return (
    <>
      <UnstyledButton
        onClick={() => setOpened((o) => !o)}
        className={classes.control}
      >
        <Group position='apart' spacing={0}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Icon className={classes.linkIcon} strokeWidth={1.5} />
            <Box>{label}</Box>
          </Box>
          {hasLinks && (
            <ChevronIcon
              className={classes.chevron}
              size={14}
              style={{
                transform: opened
                  ? `rotate(${theme.dir === 'rtl' ? -90 : 90}deg)`
                  : 'none',
              }}
            />
          )}
        </Group>
      </UnstyledButton>
      {hasLinks ? <Collapse in={opened}>{items}</Collapse> : null}
    </>
  );
}

interface NavbarLinksGroupProps {
  icon: Icon;
  label: string;
  sublinks: SubLink[];
  active: string;
  setActive: (label: string) => void;
}
const NavbarLinksGroup = (props: NavbarLinksGroupProps) => {
  const { icon, label, sublinks, active, setActive } = props;

  return (
    <>
      <LinksGroup
        icon={icon}
        label={label}
        links={sublinks}
        active={active}
        setActive={setActive}
      />
    </>
  );
};

export default NavbarLinksGroup;
